import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _typeof from "@babel/runtime/helpers/typeof";
import { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import useRefCallback from '@hzdg/use-ref-callback';
import 'intersection-observer'; // polyfill

function useIntersectionThreshold(threshold) {
  return useMemo(function () {
    return threshold;
  },
  // Note: We spread `threshold` value(s) as dependencies because
  // `threshold` might be an array that is not referentially stable
  // across renders, but we don't care if it isn't;
  // we only care if its deconstructed values are.
  Array.isArray(threshold) ? threshold : [threshold]);
}
function useIntersectionObserverInit() {
  var config = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  var _config$root = config.root,
    root = _config$root === void 0 ? undefined : _config$root,
    _config$rootMargin = config.rootMargin,
    rootMargin = _config$rootMargin === void 0 ? undefined : _config$rootMargin;
  var threshold = useIntersectionThreshold(config.threshold);
  return useMemo(function () {
    var init;
    if (root || rootMargin || threshold) {
      init = {};
      if (root) init.root = root;
      if (rootMargin) init.rootMargin = rootMargin;
      if (threshold) init.threshold = threshold;
      return init;
    }
  }, [root, rootMargin, threshold]);
}
function useIntersection(providedRefOrHandlerOrConfig, handlerOrConfig, maybeConfig) {
  var changeHandler = useRef(null);
  var providedRef = null;
  var config = undefined;
  if (providedRefOrHandlerOrConfig) {
    if (typeof providedRefOrHandlerOrConfig === 'function') {
      changeHandler.current = providedRefOrHandlerOrConfig;
      if (handlerOrConfig) {
        if ('current' in handlerOrConfig) {
          providedRef = handlerOrConfig;
          config = maybeConfig;
        } else if (_typeof(handlerOrConfig) === 'object') {
          config = handlerOrConfig;
        }
      }
    } else if ('current' in providedRefOrHandlerOrConfig) {
      providedRef = providedRefOrHandlerOrConfig;
      if (typeof handlerOrConfig === 'function') {
        changeHandler.current = handlerOrConfig;
        config = maybeConfig;
      } else if (_typeof(handlerOrConfig) === 'object') {
        config = handlerOrConfig;
      }
    } else if (_typeof(providedRefOrHandlerOrConfig) === 'object') {
      config = providedRefOrHandlerOrConfig;
    }
  }
  var _useRefCallback = useRefCallback(null),
    _useRefCallback2 = _slicedToArray(_useRefCallback, 2),
    ref = _useRefCallback2[0],
    setRef = _useRefCallback2[1];
  if (providedRef) setRef(providedRef.current);
  var subscribed = useRef(null);
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    intersects = _useState2[0],
    setIntersects = _useState2[1];
  var handleIntersectionChange = useCallback(
  /**
   * `handleIntersectionChange` will update the current change handler
   * with a new `IntersectionObserverEntry` whenever the observed element's
   * intersection with the ancestor element or viewport changes.
   */
  function handleIntersectionChange(entry) {
    if (!subscribed.current) return;
    var cb = changeHandler.current;
    if (typeof cb === 'function') {
      cb(entry);
    } else {
      setIntersects(entry.isIntersecting);
    }
  }, []);
  var intersectionObserverInit = useIntersectionObserverInit(config);
  var isSubscribed = ref.current === subscribed.current;
  useEffect(
  /**
   * `subscribeIfNecessary` will run to determine if we need to
   * subscribe to intersection events on an element. If we are already
   * subscribed to the element, it will do nothing.
   */
  function subscribeIfNecessary() {
    var currentElement = ref.current;
    if (currentElement) {
      var observer = new IntersectionObserver(function (entries) {
        var _iteratorNormalCompletion = true;
        var _didIteratorError = false;
        var _iteratorError = undefined;
        try {
          for (var _iterator = entries[Symbol.iterator](), _step; !(_iteratorNormalCompletion = (_step = _iterator.next()).done); _iteratorNormalCompletion = true) {
            var _entry = _step.value;
            if (_entry.target === currentElement) {
              return handleIntersectionChange(_entry);
            }
          }
        } catch (err) {
          _didIteratorError = true;
          _iteratorError = err;
        } finally {
          try {
            if (!_iteratorNormalCompletion && _iterator.return != null) {
              _iterator.return();
            }
          } finally {
            if (_didIteratorError) {
              throw _iteratorError;
            }
          }
        }
      }, intersectionObserverInit);
      observer.observe(currentElement);
      subscribed.current = currentElement;
      return function unsubscribe() {
        subscribed.current = null;
        if (currentElement) {
          observer.unobserve(currentElement);
        }
      };
    }
  }, [isSubscribed, ref, handleIntersectionChange, intersectionObserverInit]);
  if (!providedRef) {
    if (changeHandler.current) {
      return setRef;
    } else {
      return [intersects, setRef];
    }
  } else if (!changeHandler.current) {
    return intersects;
  }
}
export default useIntersection;